import { z } from 'zod';
import { doorTypeIds, frameColourNames } from '../../doors.js';
import { productDataPrefix } from '../../regions.js';
/** Live GI `frameColour` names — derived from the colour table in `doors.ts`. */
export const frameColours = frameColourNames;
const metaDataItems = z
    .array(z.object({
    order: z.number().int().positive(),
    description: z.string().trim().min(1),
}))
    .min(1);
const doorMetaDataSchema = z.object({
    colorSelection: z.object({ imageCdnId: z.string().min(1) }),
    features: z.object({ imageCdnId: z.string().min(1) }),
    whatsIncluded: z.object({ items: metaDataItems }),
});
/** GI `doorType` strings (lower-cased) → planner door type ids. */
const giDoorTypes = {
    'barrier door': 'barrier',
    'security door': 'security',
};
/**
 * Maps a raw GI `doorType` value to a planner door type id, or `undefined`
 * when the value is not one the feed knows. Shared by the feed schema and the
 * admin portal so both bucket rows the same way.
 */
export function parseDoorType(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim().toLowerCase();
    return giDoorTypes[trimmed] ?? doorTypeIds.find((id) => id === trimmed);
}
/**
 * Door catalogue from `PS-SECBAR-DOORS_StockItems`. Input is the raw OData
 * row (padded ids, numeric strings, `metaData` as an embedded JSON string).
 * Output trims the id, coerces mm and price, maps door type and Active.
 */
export const productRowSchema = z
    .object({
    inventoryID: z.string().trim().min(1),
    description: z.string().trim().min(1),
    itemStatus: z.string(),
    retailPrice: z.coerce.number().nonnegative(),
    doorType: z
        .string()
        .trim()
        .transform((value) => parseDoorType(value) ?? value)
        .pipe(z.enum(doorTypeIds)),
    minDoorWidth: z.coerce.number().int(),
    maxDoorWidth: z.coerce.number().int(),
    minDoorHeight: z.coerce.number().int(),
    maxDoorHeight: z.coerce.number().int(),
    frameColour: z.string().trim().pipe(z.enum(frameColours)),
    bunningsItemNumber: z.string(),
    bunningsPrimaryBarcode: z.string(),
    metaData: z.preprocess((value) => {
        if (typeof value !== 'string') {
            return value;
        }
        try {
            return JSON.parse(value);
        }
        catch {
            return undefined;
        }
    }, doorMetaDataSchema),
})
    .superRefine((row, ctx) => {
    if (row.minDoorWidth > row.maxDoorWidth) {
        ctx.addIssue({
            code: 'custom',
            message: 'minDoorWidth must be <= maxDoorWidth',
            path: ['minDoorWidth'],
        });
    }
    if (row.minDoorHeight > row.maxDoorHeight) {
        ctx.addIssue({
            code: 'custom',
            message: 'minDoorHeight must be <= maxDoorHeight',
            path: ['minDoorHeight'],
        });
    }
})
    .transform((row) => ({
    ...row,
    Active: row.itemStatus === 'Active',
}));
export const productsFeed = {
    name: 'products',
    genericInquiry: 'PS-SECBAR-DOORS_StockItems',
    select: [
        'inventoryID',
        'description',
        'itemStatus',
        'retailPrice',
        'doorType',
        'minDoorWidth',
        'maxDoorWidth',
        'minDoorHeight',
        'maxDoorHeight',
        'frameColour',
        'bunningsItemNumber',
        'bunningsPrimaryBarcode',
        'metaData',
    ],
    rowSchema: productRowSchema,
    s3Key: (region) => `${productDataPrefix(region)}/products.json`,
};
//# sourceMappingURL=products.js.map